<template>
  <div v-if="isExploitant">
    <!-- Alerts -->
    <div class="alert alert-danger" role="alert" v-if="isBrouillon && !isRejet && !isRefus && !isDatesParcValid && !isAllDatesParcNull">
      La publication des données sur Géorisques ne pourra se faire que si vous remplissez <u>les dates administratives requises</u>.
    </div>
    <div class="alert alert-warning" role="alert" v-if="isBrouillon">
      Votre parc est en cours de saisie. Les données modifiées ne seront envoyées à GéoRisques qu'après republication de la fiche parc.
    </div>
    <div class="alert alert-danger" role="alert" v-if="isDepublie">
      Votre parc étant {{ statutDepublieLibelle }}, il ne sera pas publié sur GéoRisques.
    </div>
    <div class="alert alert-info" role="alert" v-if="isSaisieNonDemarree">
      La saisie de votre parc n'a pas encore démarrée. Pour commencer à remplir les données, cliquer sur le bouton Modifier.
    </div>
    <div
      class="alert alert-warning"
      role="alert"
      v-if="isAnteDebutConstruction"
    >
      Les données techniques sont susceptibles d'évoluer tant que le
      chantier de construction n'a pas débuté.
    </div>
    <div class="alert alert-info" role="alert" v-if="isLock">
      La saisie du parc est bloquée par l'utilisateur {{ parc.mailVerrou }}
    </div>

    <!-- Boutons -->
    <div
      class="btn-group mb-4 parc-btn"
      role="group"
      aria-label="Basic example"
      v-if="isExploitant"
    >
      <b-button
        class="me-2 my-2 rounded"
        variant="success"
        size="sm"
        icon
        id="exploitant-show-all"
        @click="showModalAll()"
      >
        <i class="fa fa-info"></i>
        <b-tooltip target="#exploitant-show-all">
          Afficher la vue d'ensemble des données du parc {{ parc.id }}
        </b-tooltip>
      </b-button>
      <b-button
        type="button"
        class="me-2 my-2 btn btn-success rounded"
        v-if="isBrouillon || isAnnule || isSaisieNonDemarree || isDepublie"
        :disabled="isLock"
        id="exploitant-editer"
        @click="onClickEditer()"
      >
        <span class="parc-btn-label">Modifier</span>
        <i class="fa fa-edit parc-icon"></i>
        <b-tooltip target="#exploitant-editer">
          Éditer les informations du parcs
        </b-tooltip>
      </b-button>

      <span
        class="d-inline-block"
        tabindex="0"
        id="exploitant-actions-btn"
      >
        <b-tooltip target="#exploitant-actions-btn">
          {{ disablePublishBtn ? "Veuillez compléter les différents formulaires" : '' }}
        </b-tooltip>
        <p-button
          type="button"
          class="my-2 me-2 btn btn-success rounded"
          v-if="isBrouillon && isPublishable"
          id="exploitant-publier"
          @click="showPublishModal()"
          title="Diffuser les données sur Géorisques"
          :disabled="disablePublishBtn"
        >
          <span class="parc-btn-label">Publier les données</span>
          <i class="fa fa-upload parc-icon"></i>
          <b-tooltip target="#exploitant-publier">
            Diffuser les données sur Géorisques
          </b-tooltip>
        </p-button>
      </span>
      <b-button
        variant="success"
        class="my-2 rounded"
        v-if="isPublie"
        @click="showUnpublishModal()"
        title="Rendre la modification des informations accessible"
      >
        <span>Reprendre la saisie du parc</span>
      </b-button>

      <b-button
        variant="success"
        class="my-2 me-2 rounded"
        id="exploitant-renouvellement-btn"
        :disabled="isLock"
        @click="onClickRenouvellement"
        v-if="(isBrouillon || isAnnule || isSaisieNonDemarree || isDepublie)"
      >
        <span class="parc-btn-label">Renouvellement(s)</span>
        <i class="fa fa-file parc-icon"></i>
        <b-tooltip target="#exploitant-renouvellement-btn">
          {{ isRenouvellement ? 'Consulter les renouvellements' : 'Indiquer un renouvellement' }}
        </b-tooltip>
      </b-button>
    </div>
  </div>
</template>

<script setup>
import {BButton, BTooltip, useModal} from "bootstrap-vue-next";
import {useReactiveStoreGetters} from "@/composables/useStore";
import {computed} from "vue";

const {show: showPublishModal} = useModal('modal-publier')
const {show: showUnpublishModal} = useModal('modal-depublier')

defineProps({
  isPublishable: Boolean,
  showModalAll: Function,
  onClickEditer: Function,
  onClickRenouvellement: Function,
})

// Getters
const {
  parc,
  isExploitant,
  isBrouillon,
  isAnnule,
  isDepublie,
  isSaisieNonDemarree,
  isRejet,
  isRefus,
  isCessationActivite,
  isRenouvellement,
  isEolienneDeclarationValid,
  isPosteLivraisonDeclarationValid,
  isPublie,
  isLock,
  isAnteDebutConstruction,
} = useReactiveStoreGetters([
  'parc',
  'isExploitant',
  'isBrouillon',
  'isAnnule',
  'isDepublie',
  'isSaisieNonDemarree',
  'isRejet',
  'isRefus',
  'isCessationActivite',
  'isRenouvellement',
  'isEolienneDeclarationValid',
  'isPosteLivraisonDeclarationValid',
  'isPublie',
  'isLock',
  'isAnteDebutConstruction',
]);

const isDatesParcValid = computed(() => {
  // Mise en service
  if (parc.value.dateMiseEnService !== null && parc.value.dateDebutConstruction === null) {
    return false;
  }
  // Déclaration d'ouverture du chantier de construction
  if (parc.value.dateDebutConstruction !== null && parc.value.dateDelivranceAutorisation === null) {
    return false;
  }
  // Arrêté d'autorisation ICPE
  if (parc.value.dateDelivranceAutorisation !== null && parc.value.dateDelivranceAvisAutoriteEnvironnementale === null) {
    return false;
  }
  // Délivrance de l'avis de l'autorité environnementale
  return !(parc.value.dateDelivranceAvisAutoriteEnvironnementale !== null && parc.value.dateDepotDemandeAutorisation === null);
});

const disablePublishBtn = computed(() => {
  return (
    isDatesParcValid.value === false ||
    isEolienneDeclarationValid.value === false ||
    isPosteLivraisonDeclarationValid.value === false
  );
});

const isAllDatesParcNull = computed(() => {
  return (parc.value.dateDepotDemandeAutorisation == null) && (parc.value.dateDelivranceAvisAutoriteEnvironnementale == null)
    && (parc.value.dateDelivranceAutorisation == null) && (parc.value.dateDebutConstruction == null) && (parc.dateMiseEnService == null);
});

const statutDepublieLibelle = computed(() => {
  if (isRefus.value) return "refusé";
  if (isRejet.value) return "rejeté";
  if (isCessationActivite.value) return "en cessation d'activité";
  return "dépublié";
});

</script>
